import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";

/**
 * Componente Flashcards - FASE 1
 * Cards interativos com funcionalidade de virar (pergunta -> resposta)
 * e navegação entre cards
 */
const Flashcards = ({ flashcardsData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  // Reset ao mudar de dados (novo vídeo)
  useEffect(() => {
    setCurrentIndex(0);
    setIsFlipped(false);
  }, [flashcardsData]);

  if (!flashcardsData || flashcardsData.length === 0) {
    return (
      <Card className="card">
        <CardContent className="p-8 text-center">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Flashcards não disponíveis para este vídeo</p>
        </CardContent>
      </Card>
    );
  }

  const currentCard = flashcardsData[currentIndex];
  const totalCards = flashcardsData.length;

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (currentIndex < totalCards - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  return (
    <Card className="card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-600" />
            Flashcards de Fixação
          </CardTitle>
          <Badge variant="secondary">
            {currentIndex + 1} de {totalCards}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-lg p-4">
          <p className="text-sm text-purple-800">
            💡 Clique no card para revelar a resposta. Use os botões para navegar.
          </p>
        </div>

        {/* Flashcard interativo */}
        <div className="relative h-80 perspective-1000">
          <div
            onClick={handleFlip}
            className={`absolute inset-0 w-full h-full transition-transform duration-500 transform-style-3d cursor-pointer ${
              isFlipped ? 'rotate-y-180' : ''
            }`}
            style={{
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Frente do card (Pergunta) */}
            <div
              className="absolute inset-0 w-full h-full backface-hidden bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-xl p-8 flex flex-col items-center justify-center"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <div className="text-white text-center space-y-4">
                <div className="inline-block bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 mb-4">
                  <p className="text-sm font-semibold uppercase tracking-wide">Pergunta</p>
                </div>
                <p className="text-xl font-semibold leading-relaxed">
                  {currentCard.question}
                </p>
                <div className="mt-8 text-sm opacity-75">
                  👆 Clique para ver a resposta
                </div>
              </div>
            </div>

            {/* Verso do card (Resposta) */}
            <div
              className="absolute inset-0 w-full h-full backface-hidden bg-gradient-to-br from-green-500 to-teal-600 rounded-xl shadow-xl p-8 flex flex-col items-center justify-center"
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }}
            >
              <div className="text-white text-center space-y-4">
                <div className="inline-block bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 mb-4">
                  <p className="text-sm font-semibold uppercase tracking-wide">Resposta</p>
                </div>
                <p className="text-lg leading-relaxed">
                  {currentCard.answer}
                </p>
                <div className="mt-8 text-sm opacity-75">
                  👆 Clique para voltar à pergunta
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Controles de navegação */}
        <div className="flex items-center justify-between gap-3">
          <Button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>

          <Button
            onClick={handleReset}
            variant="ghost"
            size="icon"
            title="Voltar ao início"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>

          <Button
            onClick={handleNext}
            disabled={currentIndex === totalCards - 1}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            Próximo
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Progress indicator */}
        <div className="flex gap-1 justify-center">
          {flashcardsData.map((_, idx) => (
            <div
              key={idx}
              className={`h-2 rounded-full transition-all ${
                idx === currentIndex
                  ? 'w-8 bg-purple-600'
                  : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default Flashcards;
